"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import WorldMap from "./WorldMap";
import NodeCard from "./NodeCard";
import Dropdown from "./Dropdown";
import { measureLatency, probe, isAutomated, whenIdle } from "./latency";
import type { Cpu, Location, Reading, Region } from "./types";

const ALL = "All";
/** Timed rounds per location, on top of one discarded warm-up round. */
const ROUNDS = 3;

/** Sorting key: measured latency first (ascending), then unknowns, then n/a. */
function rank(r: Reading): number {
    if (typeof r === "number") return r;
    return r === "fail" ? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER - 1;
}

/**
 * Owns the latency readings for the whole locations block, so the world map and
 * the node cards always agree on the numbers and on which location is
 * recommended. Probing runs once on mount and on demand from any refresh chip.
 */
export default function LocationsExplorer({
    regions,
    hardwareHeading,
}: {
    regions: Region[];
    /** Heading rendered above the node grid, so the copy stays in the page. */
    hardwareHeading?: ReactNode;
}) {
    const [pings, setPings] = useState<Record<string, Reading>>({});
    /** Every location has been probed to the end, so `best` can settle. */
    const [probed, setProbed] = useState(false);
    /** null = follow the recommendation; a string means the visitor chose. */
    const [regionSel, setRegionSel] = useState<string | null>(null);

    const all = regions.flatMap((r) => r.locations);
    const testable = all.filter((l) => !l.soon && l.probe);

    useEffect(() => {
        const targets = regions.flatMap((r) => r.locations).filter((l) => !l.soon && l.probe);
        if (isAutomated() || targets.length === 0) return;
        let cancelled = false;
        (async () => {
            await whenIdle();
            // Round-robin, one sample per location per round: probing a location
            // to completion before starting the next made whichever went first
            // wear the cost of the page still loading. Sequential within a round
            // because parallel probes share one pipe and inflate each other.
            const fastest: Record<string, number> = {};
            const dead = new Set<string>();
            for (let round = 0; round <= ROUNDS; round++) {
                for (const l of targets) {
                    if (cancelled) return;
                    if (dead.has(l.code)) continue;
                    let ms: number;
                    try {
                        ms = await probe(l.probe!);
                    } catch {
                        dead.add(l.code);
                        setPings((p) => ({ ...p, [l.code]: "fail" }));
                        continue;
                    }
                    if (cancelled) return;
                    if (round === 0) continue; // DNS and the TLS handshake
                    fastest[l.code] = Math.round(Math.min(fastest[l.code] ?? Infinity, ms));
                    setPings((p) => ({ ...p, [l.code]: fastest[l.code] }));
                }
            }
            setProbed(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [regions]);

    const retest = useCallback(
        (code: string) => {
            const loc = regions.flatMap((r) => r.locations).find((l) => l.code === code);
            if (!loc?.probe) return;
            setPings((p) => ({ ...p, [code]: null }));
            void (async () => {
                let reading: Reading;
                try {
                    reading = await measureLatency(loc.probe!);
                } catch {
                    reading = "fail";
                }
                setPings((p) => ({ ...p, [code]: reading }));
            })();
        },
        [regions],
    );

    // Wait for every probe to land before crowning one, so the badge does not
    // hop between locations while the measurements are still coming in.
    const measured = Object.entries(pings).filter((e): e is [string, number] => typeof e[1] === "number");
    const settled = probed && testable.every((l) => typeof pings[l.code] !== "undefined" && pings[l.code] !== null);
    const best = settled && measured.length > 0 ? measured.reduce((a, b) => (a[1] <= b[1] ? a : b))[0] : null;

    const regionOf = (code: string) => regions.find((r) => r.locations.some((l) => l.code === code))?.name;
    const bestRegion = best ? regionOf(best) : undefined;
    /** Until the visitor picks, the list opens on their fastest region. */
    const region = regionSel ?? bestRegion ?? ALL;

    const nodes: { region: string; location: Location; cpu: Cpu; first: boolean }[] = regions.flatMap((r) =>
        r.locations.flatMap((l) =>
            (l.soon ? [] : l.cpus).map((cpu, i) => ({ region: r.name, location: l, cpu, first: i === 0 })),
        ),
    );

    const shown = nodes
        .filter((n) => region === ALL || n.region === region)
        .sort((a, b) => rank(pings[a.location.code] ?? null) - rank(pings[b.location.code] ?? null));

    /** Reveal a location's hardware even if the current region filter hides it. */
    const focus = (code: string) => {
        setRegionSel(regionOf(code) ?? ALL);
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                const el = document.getElementById(`loc-${code}`);
                if (!el) return;
                const lenis = (window as unknown as { lenis?: { scrollTo: (t: Element, o?: object) => void } }).lenis;
                if (lenis) lenis.scrollTo(el, { offset: -110 });
                else el.scrollIntoView({ behavior: "smooth", block: "start" });
            }),
        );
    };

    return (
        <>
            <WorldMap regions={regions} pings={pings} best={best} onRetest={retest} onFocus={focus} />

            <div className="mt-20">{hardwareHeading}</div>

            <div className="mt-8 flex justify-center">
                <div className="w-full sm:w-64">
                    <Dropdown
                        label="Region"
                        value={region}
                        options={[ALL, ...regions.map((r) => r.name)]}
                        onChange={setRegionSel}
                    />
                </div>
            </div>

            {shown.length === 0 ? (
                <p className="text-gray-500 font-poppins text-sm mt-8">No nodes match those filters.</p>
            ) : (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shown.map((n) => (
                        <div
                            key={`${n.location.code}-${n.cpu.name}`}
                            id={n.first ? `loc-${n.location.code}` : undefined}
                            className="scroll-mt-28 flex"
                        >
                            <NodeCard
                                location={n.location}
                                region={n.region}
                                cpu={n.cpu}
                                reading={pings[n.location.code] ?? null}
                                recommended={n.location.code === best}
                                onRetest={() => retest(n.location.code)}
                            />
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
